/**
 * Authentication user types matching backend UserProfileDto
 * These types match what the /auth/me endpoint returns after Keycloak migration
 */

import { UserRole } from '@/types';

/**
 * Raw Keycloak role strings that appear in
 * `LantbrukareUser.agronodKonton[].Affarspartners[].Roller[]`.
 *
 * These are the Keycloak-side identifiers, NOT the mapped `UserRole` enum.
 * Frontend code that reads `Roller[]` directly (e.g. `useIsRadgivare` for
 * per-konto checks) should reference these constants. The backend's
 * `LANTBRUKARE_ROLE_MAP` uses the same strings; renaming one without updating
 * the other silently breaks the mapping with no compile-time signal.
 */
export const LANTBRUKARE_KEYCLOAK_ROLES = {
  RADGIVARE: 'r-agrokoll',
} as const;

export interface AgronodKontoAffarspartners {
  Id: string;
  Roller: string[];
}

export interface AgronodKonton {
  KontoId: string;
  KontoNamn: string;
  Affarspartners: AgronodKontoAffarspartners[];
}

/**
 * Base user profile fields (common to both user types)
 */
interface BaseUserProfile {
  id: string;
  name: string;
  roles: UserRole[];  // Extracted and mapped roles from Keycloak
  userType: 'B2B' | 'Lantbrukare';
}

export interface LantbrukareUser extends BaseUserProfile {
  userType: 'Lantbrukare';
  email: string;
  ssn: string;
  emailVerified: boolean;
  registered: boolean;
  agronodKonton: AgronodKonton[];
}

export interface B2bUser extends BaseUserProfile {
  userType: 'B2B';
  affarspartnerId: string;
}

export type AuthUser = LantbrukareUser | B2bUser;

// Type guards
export function isLantbrukareUser(user: AuthUser): user is LantbrukareUser {
  return user.userType === 'Lantbrukare';
}

export function isB2bUser(user: AuthUser): user is B2bUser {
  return user.userType === 'B2B';
}

// Helper functions for safe user data access
export function getUserEmail(user: AuthUser | undefined | null): string | undefined {
  if (!user) return undefined;
  if (isLantbrukareUser(user)) return user.email;
  if (isB2bUser(user)) return undefined; // B2B users don't have email
  return undefined;
}

export function getUserDisplayName(user: AuthUser | undefined | null): string | undefined {
  if (!user) return undefined;
  return user.name;
}

/**
 * Get farm/organization name for display
 * For Lantbrukare: Returns first agronodKonton.KontoNamn
 * For B2B: Returns null (no equivalent)
 * TODO: Add account selector when user has multiple agronodKonton
 */
export function getFarmName(user: AuthUser | undefined | null): string | null {
  if (!user) return null;
  if (isLantbrukareUser(user)) {
    return user.agronodKonton[0]?.KontoNamn ?? null;
  }
  return null; // B2B users don't have farm names
}

/**
 * Extract highest user role for permission checks.
 * Returns the highest-level role from the user's roles array.
 *
 * Role hierarchy (from highest to lowest):
 * - AGROKOLL_SYSTEM_ADMIN (level 3)
 * - AGROKOLL_B2B (level 2)
 * - AGROKOLL_ADMIN (level 1)
 * - AGROKOLL_OWNER (level 1)
 */
export function getUserRole(user: AuthUser | undefined | null): UserRole | null {
  if (!user || !user.roles || user.roles.length === 0) return null;

  // Role hierarchy levels
  const ROLE_LEVELS: Record<UserRole, number> = {
    [UserRole.AGROKOLL_OWNER]: 1,
    [UserRole.AGROKOLL_ADMIN]: 1,
    [UserRole.AGROKOLL_RADGIVARE]: 1,
    [UserRole.AGROKOLL_B2B]: 2,
    [UserRole.AGROKOLL_SYSTEM_ADMIN]: 3,
  };

  // Find role with highest level
  let highestRole: UserRole = user.roles[0]!; // Safe: checked length > 0 above
  let highestLevel = ROLE_LEVELS[highestRole] || 0;

  for (const role of user.roles) {
    const currentLevel = ROLE_LEVELS[role] || 0;
    if (currentLevel > highestLevel) {
      highestRole = role;
      highestLevel = currentLevel;
    }
  }

  return highestRole;
}

/**
 * Get all user roles (useful for checking if user has any specific role)
 */
export function getUserRoles(user: AuthUser | undefined | null): UserRole[] {
  if (!user || !user.roles) return [];
  return user.roles;
}
